import { HeroMinimal, BookingCTA } from "../components/sections";
import { SectionHeader, FeatureCard, Stat } from "../components/sections";
import {
  Search,
  Users,
  Database,
  Shield,
  Filter,
  Target,
  Zap,
  CheckCircle,
} from "lucide-react";

const dataSections = [
  {
    title: "Company Discovery",
    description:
      "Identify target companies using firmographics, technographics, intent signals, hiring data, funding events, and lookalike modeling.",
    capabilities: [
      "Firmographic filtering",
      "Technographic detection",
      "Intent signal monitoring",
      "Hiring signal tracking",
      "Funding & M&A alerts",
      "Lookalike modeling",
    ],
    icon: Search,
  },
  {
    title: "Contact Discovery",
    description:
      "Map decision-makers, influencers, and champions within target accounts. Identify buying committee roles and reporting lines.",
    capabilities: [
      "Org chart mapping",
      "Buyer role identification",
      "Champion detection",
      "Reporting line inference",
      "Contact prioritization",
      "Gap analysis",
    ],
    icon: Users,
  },
  {
    title: "Multi-Source Enrichment",
    description:
      "Layer firmographic, technographic, contact, social, and behavioral data from multiple providers. Resolve conflicts, score quality.",
    capabilities: [
      "Provider orchestration",
      "Data fusion & dedup",
      "Conflict resolution",
      "Quality scoring",
      "Freshness tracking",
      "Coverage optimization",
    ],
    icon: Database,
  },
  {
    title: "Contact Verification",
    description:
      "Real-time email and phone validation with deliverability scoring, catch-all detection, and risk classification.",
    capabilities: [
      "SMTP verification",
      "Catch-all detection",
      "Deliverability scoring",
      "Risk classification",
      "Bounce prediction",
      "Continuous re-verification",
    ],
    icon: Shield,
  },
  {
    title: "Segmentation",
    description:
      "Dynamic micro-segments combining fit, intent, behavior, engagement, and custom criteria. Auto-refreshing as data changes.",
    capabilities: [
      "Fit-based segments",
      "Intent cohorts",
      "Engagement tiers",
      "Behavioral clusters",
      "Custom rule builder",
      "Auto-refresh schedules",
    ],
    icon: Filter,
  },
  {
    title: "Research Context",
    description:
      "AI-generated prospect dossiers: company strategy, tech stack, initiatives, challenges, and personalized talking points.",
    capabilities: [
      "Strategic initiative mapping",
      "Tech stack analysis",
      "Challenge inference",
      "Talking point generation",
      "Competitive positioning",
      "Social proof matching",
    ],
    icon: Target,
  },
];

const dataLifecycleSteps = [
  {
    step: "Define ICP",
    description:
      "Codify ideal customer profile, buyer personas, segmentation criteria, and exclusion rules",
    icon: Target,
  },
  {
    step: "Discover Companies",
    description:
      "Multi-source company identification using firmographics, technographics, and signals",
    icon: Search,
  },
  {
    step: "Identify Buyers",
    description:
      "Map decision-makers, champions, and influencers within target accounts",
    icon: Users,
  },
  {
    step: "Enrich Records",
    description:
      "Layer firmographic, technographic, contact, and behavioral data from multiple providers",
    icon: Database,
  },
  {
    step: "Verify Contacts",
    description:
      "Real-time email/phone validation with deliverability scoring and risk classification",
    icon: Shield,
  },
  {
    step: "Segment",
    description:
      "Dynamic micro-segmentation by fit, intent, behavior, and engagement",
    icon: Filter,
  },
  {
    step: "Personalize",
    description:
      "Context-aware content generation using research intelligence for each segment",
    icon: Zap,
  },
];

const qualityMetrics = [
  {
    label: "Email Deliverability",
    value: "Verified",
    description: "Review verification status before sending",
  },
  {
    label: "Contact Accuracy",
    value: "Checked",
    description: "Check contact information across sources",
  },
  {
    label: "Data Freshness",
    value: "Refreshed",
    description: "Review stale records and update context",
  },
  {
    label: "Coverage",
    value: "Focused",
    description: "Prioritize accounts that fit your ICP",
  },
];

function DataProspectingPage() {
  return (
    <>
      <HeroMinimal
        title="The Right Outreach Starts With the Right Data."
        subtitle="Bad data produces bad outbound. Our system ensures every outreach is built on verified, enriched, and contextualized intelligence, with relevance before volume."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See AI & Automation"
        secondaryCtaHref="/technology/ai-automation"
      />

      {/* Data Lifecycle */}
      <section className="section" aria-labelledby="lifecycle-title">
        <div className="container">
          <SectionHeader
            id="lifecycle-title"
            eyebrow="Data Lifecycle"
            title="Find → Identify → Enrich → Verify → Segment → Personalize"
            description="A structured pipeline that transforms raw market data into outreach-ready intelligence."
            align="center"
          />
          <ol className="lifecycle-grid" aria-label="Data lifecycle">
            {dataLifecycleSteps.map((item, index) => (
              <li key={item.step} className="lifecycle-card card">
                <span className="step-number">0{index + 1}</span>
                <h3 className="h3 flex items-center gap-3">
                  <item.icon
                    size={21}
                    className="text-accent"
                    aria-hidden="true"
                  />
                  {item.step}
                </h3>
                <p>{item.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      {/* Data Capabilities */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="capabilities-title"
      >
        <div className="container">
          <SectionHeader
            id="capabilities-title"
            eyebrow="Data Capabilities"
            title="Six Pillars of Data Intelligence"
            description="Each pillar can be engaged independently. Together they form a complete data foundation for outbound."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {dataSections.map((section, index) => (
              <FeatureCard
                key={section.title}
                icon={<section.icon size={24} className="text-accent" />}
                title={section.title}
                description={section.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              />
            ))}
          </div>

          <div className="max-w-5xl mx-auto mt-16 space-y-6">
            {dataSections.map((section, index) => (
              <details
                key={section.title}
                className="group card p-6 animate-slide-up"
                style={{ animationDelay: `${(index + 6) * 50}ms` }}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center gap-3">
                    <section.icon
                      size={24}
                      className="text-accent"
                      aria-hidden="true"
                    />
                    <h3 className="h3 text-text-primary">{section.title}</h3>
                  </div>
                  <span className="text-text-muted">Capabilities →</span>
                </summary>
                <div className="mt-4 pt-4 border-t border-border grid md:grid-cols-2 gap-3 animate-slide-down">
                  {section.capabilities.map((cap, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-text-secondary"
                    >
                      <CheckCircle
                        size={14}
                        className="text-accent flex-shrink-0"
                        aria-hidden="true"
                      />
                      {cap}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="section" aria-labelledby="quality-title">
        <div className="container">
          <SectionHeader
            id="quality-title"
            eyebrow="Quality Standards"
            title="Relevance Over Volume"
            description="We measure what matters: deliverability, accuracy, freshness, and coverage. Not list size."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
            {qualityMetrics.map((metric, index) => (
              <Stat
                key={metric.label}
                value={metric.value}
                label={metric.label}
                description={metric.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="data-philosophy-title"
      >
        <div className="container">
          <SectionHeader
            id="data-philosophy-title"
            eyebrow="Philosophy"
            title="Data Discipline = Outbound Performance"
            description="Every failed delivery, wrong contact, or irrelevant message wastes sales capacity. Our data discipline is the foundation of system performance."
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {[
              {
                title: "Verification First",
                description:
                  "No outreach without verified contact. Real-time validation on every send.",
              },
              {
                title: "Multi-Source Truth",
                description:
                  "No single provider. Cross-reference, resolve conflicts, score confidence.",
              },
              {
                title: "Continuous Hygiene",
                description:
                  "Automated bounce handling, complaint processing, and re-verification cycles.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="card-elevated p-6 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h3 text-text-primary mb-2">{item.title}</h3>
                <p className="body text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default DataProspectingPage;
